import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../api/authService';
import { userSettingService } from '../api/userSettings';
import { postLoginDestination } from '../utils/postLoginRedirect';

const AuthCallback = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const authContext = useAuth();
    const { login } = authContext || {};

    // The effect re-runs whenever `login` changes identity (AuthProvider rebuilds it on every
    // render) and twice under StrictMode. Handle the callback once: concurrent runs raced, and
    // the second could navigate to /dashboard after the first had sent the user to a pending
    // AI-app consent page.
    const handledRef = useRef(false);

    useEffect(() => {
        if (handledRef.current) return;
        handledRef.current = true;
        const handleCallback = async () => {
            const token = searchParams.get('token');
            const firstName = searchParams.get('firstName');
            const lastName = searchParams.get('lastName');
            
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                // Store token first
                localStorage.setItem('token', token);
                
                // Get user data
                const userData = await authService.getCurrentUserFromCallback();
                
                const setLocalStorageWithExpiry = (user: any, token: string) => {
                    const expiryTime = new Date();
                    expiryTime.setHours(expiryTime.getHours() + 24);

                    const item = {
                        user,
                        token,
                        expiry: expiryTime.getTime()
                    };

                    localStorage.setItem('userAuth', JSON.stringify(item));
                };

                setLocalStorageWithExpiry(userData, token);
                // login() tracks user_login (once, labelled with the provider).
                login?.(userData, token, searchParams.get('provider') ?? 'oauth');
                authService.recordSignupAttribution();

                // Same rule as Login: a pending AI-app consent first, then onboarding if unfinished.
                let needsOnboarding = false;
                try {
                    const settings = await userSettingService.getUserSettings();
                    needsOnboarding = settings.onboarding_complete === false;
                } catch {
                    // settings unavailable — fall through to the dashboard as before
                }
                const destination = postLoginDestination(needsOnboarding);
                navigate(destination, destination === '/profile-setup'
                    ? { state: { firstName: firstName || '', lastName: lastName || '' } }
                    : undefined);
            } catch (error) {
                console.error('OAuth callback error:', error);
                navigate('/login');
            }
        };

        handleCallback();
    }, [searchParams, navigate, login]);

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            fontSize: '18px'
        }}>
            Completing authentication...
        </div>
    );
};

export default AuthCallback;